// @ts-nocheck

/* eslint-disable-next-line local/require-storybook-story --
 * FabricJS canvas host, same class as widgets/ecg-viewer (docs/project-structure.md: "E2E/manual
 * verify, no unit tests"). It instantiates FabricChartJS.ECGChart onto raw <canvas> elements from a
 * live streaming buffer — no DOM locators for axe to survey either way, and no fixture can stand in
 * for the real-time ECG payload. Covered by E2E/manual verification, not Storybook. */
import _ from 'lodash';
import { Component } from 'react';

import FabricChartJS from './publicApi';
import type { ECGViewerProps } from './fabricJs.types';

/**
 * Wraps a FabricJS ECG chart as a React class component for the live-ECG modal of the studies list. Seeds toolbar state from createInitialValue, instantiates FabricChartJS.ECGChart on mount from the ecg payload's samples, beat markers, filters and diagnostic lead, rebuilds it when that payload changes, and renders the index-suffixed canvases the chart draws onto.
 */
class ECGViewer extends Component<ECGViewerProps, any> {
  declare haveCompleted: any;

  declare isClickFineGridCurrent: any;

  declare isInViewAllModeCurrent: any;

  declare isSelectingHLine: any;

  declare isSelectingVLine: any;

  declare isSelectingPattern: any;

  constructor(props: ECGViewerProps) {
    super(props);
    const initialValue = this.createInitialValue();
    this.state = {
      ...initialValue,
      ecgChart: {},
    };
  }

  /**
   * Creates the FabricJS ECG chart once the viewer mounts with a non-empty ecg payload. Builds a config from speed, amplitude, window duration and the high/notch filter values with fallbacks, picks the preview channel from the diagnostic lead when that channel is hidden, resolves annotations from the viewer object unless the full-event chart is shown, and stores the chart with sample count, speed, gain, filter and visible-channel state.
   *
   * @returns Nothing.
   */
  componentDidMount() {
    const { ecg, index, isStreaming, isLoading } = this.props;
    if (!_.isEmpty(ecg)) {
      const {
        configChart,
        missingSampleLength,
        isShowViewFullEventChart,
        formatTextTimeObj,
        timeObject,
      } = this.props;
      const { isDisplayScrollbar } = configChart;
      const {
        dataSamples,
        beatMarkers,
        viewerObject,
        hpFilters,
        notchFilters,
        hiddenChannels,
        eventPos,
        diagnosticLead,
        isShowBeatMarkers,
        speed,
        amp,
        wndDuration,
      } = ecg;
      const config = {
        speed: !_.isNil(speed) ? speed : 10,
        gain: !_.isNil(amp) ? amp : 10,
        wndDuration: !_.isNil(wndDuration) ? wndDuration : 10,
        LFF: !_.isNil(hpFilters) ? hpFilters : 0,
        HFF: !_.isNil(notchFilters) ? notchFilters : 0,
        prevChanIdx:
          hiddenChannels && hiddenChannels.length && _.includes(hiddenChannels, diagnosticLead - 1)
            ? diagnosticLead - 1
            : 1,
        isShowBeatMarkers,
        formatTextTimeObj,
      };
      const annotations =
        !isShowViewFullEventChart && viewerObject ? viewerObject.widgets : JSON.parse('null');
      const objectFunctions = {
        updateMinMaxHr: this.props.updateMinMaxHr,
        updatePrevChanIdx: () => {},
        updateVisibleChannels: () => {},
        updateSpdMenu: () => {},
        updateAmpMenu: () => {},
        handleChangeHiLoPDropdown: () => {},
        handleToggleFineGrid: () => {},
        handleARBeat: () => {},
        handleZoom: () => {},
        handleViewAll: () => {},
        toggleViewAllMode: () => {},
        updateCompletedStatus: () => {},
        updateWaitingZoomModeStatus: () => {},
        updateZoomModeStatus: () => {},
        updateARBeatModeStatus: () => {},
        updateSelectingHLineStatus: () => {},
        updateSelectingVLineStatus: () => {},
        updateSelectingPatternStatus: () => {},
        handleOpenSnackbarARBeat: () => {},
        handleCloseSnackbarARBeat: () => {},
        handlePickDate: this.props.handlePickDate,
        onCalculatedData: this.props.onCalculatedData,
        onBpmChanged: this.props.onBpmChanged,
        getStreamingData: this.getStreamingData,
      };

      // oxlint-disable-next-line import/no-named-as-default-member
      const ecgChart = new FabricChartJS.ECGChart(
        dataSamples,
        config,
        beatMarkers,
        annotations,
        objectFunctions,
        isDisplayScrollbar,
        hiddenChannels,
        eventPos,
        missingSampleLength,
        diagnosticLead,
        timeObject,
        index,
        isStreaming,
        isLoading,
      );

      const setStateObject = {
        ecgChart,
        prevChanIdx:
          hiddenChannels && hiddenChannels.length && _.includes(hiddenChannels, diagnosticLead - 1)
            ? diagnosticLead - 1
            : 1,
        dataLength: dataSamples ? dataSamples.length : 0,
        isLoadedData: true,
        spdValue: !_.isNil(speed) ? speed : 25,
        ampValue: !_.isNil(amp) ? amp : 10,
      };

      if (hiddenChannels && hiddenChannels.length) {
        _.assign(setStateObject, {
          beforeZoomVisibleChannels: hiddenChannels,
          visibleChannels: hiddenChannels,
        });
      }

      if (!_.isNil(hpFilters)) {
        _.assign(setStateObject, { hiPValue: hpFilters });
      }
      if (!_.isNil(notchFilters)) {
        _.assign(setStateObject, { loPValue: notchFilters });
      }
      if (!_.isNil(isShowBeatMarkers)) {
        _.assign(setStateObject, { isShowBeatMarkers });
      }

      this.setState(setStateObject);
    }
  }

  // *: Use componentDidUpdate to update chart data
  /**
   * Rebuilds the ECG chart when the incoming ecg payload differs from the previous render. Flags an update when the sample count, viewer object, beat markers, hidden channels, high/notch filters or speed changed or any of the first 100 samples of the three channels differ, then calls updateECGChart with the recomputed config and annotations, clears the completed, fine-grid, view-all and selection flags, and resets state from createInitialValue.
   *
   * @param prevProps - Props from the previous render, compared against the current ecg payload.
   * @returns Nothing.
   */
  componentDidUpdate(prevProps: ECGViewerProps) {
    const { ecg } = this.props;
    if (!_.isEmpty(ecg) && !_.isEmpty(prevProps.ecg)) {
      let needUpdateChartData = false;
      if (ecg.dataSamples) {
        if (!prevProps.ecg.dataSamples) {
          needUpdateChartData = true;
        } else if (ecg.dataSamples[0].length !== prevProps.ecg.dataSamples[0].length) {
          needUpdateChartData = true;
        } else if (
          JSON.stringify(ecg.viewerObject) !== JSON.stringify(prevProps.ecg.viewerObject)
        ) {
          needUpdateChartData = true;
        } else if (JSON.stringify(ecg.beatMarkers) !== JSON.stringify(prevProps.ecg.beatMarkers)) {
          needUpdateChartData = true;
        } else if (
          JSON.stringify(ecg.hiddenChannels) !== JSON.stringify(prevProps.ecg.hiddenChannels)
        ) {
          needUpdateChartData = true;
        } else if (JSON.stringify(ecg.hpFilters) !== JSON.stringify(prevProps.ecg.hpFilters)) {
          needUpdateChartData = true;
        } else if (
          JSON.stringify(ecg.notchFilters) !== JSON.stringify(prevProps.ecg.notchFilters)
        ) {
          needUpdateChartData = true;
        } else if (ecg.speed !== prevProps.ecg.speed) {
          needUpdateChartData = true;
        } else {
          // *: Differ 100 first data samples of all channels
          _.forEach(_.range(100), (index) => {
            if (
              ecg.dataSamples[0][index] !== prevProps.ecg.dataSamples[0][index] ||
              ecg.dataSamples[1][index] !== prevProps.ecg.dataSamples[1][index] ||
              ecg.dataSamples[2][index] !== prevProps.ecg.dataSamples[2][index]
            ) {
              needUpdateChartData = true;
            }
          });
        }

        if (needUpdateChartData) {
          const { ecgChart } = this.state;
          const {
            configChart,
            missingSampleLength,
            isShowViewFullEventChart,
            formatTextTimeObj,
            timeObject,
          } = this.props;
          const { isDisplayScrollbar } = configChart;
          const {
            dataSamples,
            beatMarkers,
            viewerObject,
            hpFilters,
            notchFilters,
            hiddenChannels,
            eventPos,
            diagnosticLead,
            isShowBeatMarkers,
            speed,
            amp,
            wndDuration,
          } = ecg;
          const config = {
            speed: !_.isNil(speed) ? speed : 10,
            gain: !_.isNil(amp) ? amp : 10,
            wndDuration: !_.isNil(wndDuration) ? wndDuration : 10,
            LFF: !_.isNil(hpFilters) ? hpFilters : 0,
            HFF: !_.isNil(notchFilters) ? notchFilters : 0,
            prevChanIdx:
              hiddenChannels &&
              hiddenChannels.length &&
              _.includes(hiddenChannels, diagnosticLead - 1)
                ? diagnosticLead - 1
                : 1,
            isShowBeatMarkers,
            formatTextTimeObj,
          };
          const annotations =
            !isShowViewFullEventChart && viewerObject ? viewerObject.widgets : JSON.parse('null');
          ecgChart.updateECGChart(
            dataSamples,
            config,
            beatMarkers,
            annotations,
            isDisplayScrollbar,
            hiddenChannels,
            eventPos,
            missingSampleLength,
            diagnosticLead,
            timeObject,
          );
          this.haveCompleted = false;
          this.isClickFineGridCurrent = false;
          this.isInViewAllModeCurrent = false;
          this.isSelectingHLine = false;
          this.isSelectingVLine = false;
          this.isSelectingPattern = false;
          const initialValue = this.createInitialValue();
          const setStateObject = {
            ...initialValue,
            ecgChart,
            prevChanIdx:
              hiddenChannels &&
              hiddenChannels.length &&
              _.includes(hiddenChannels, diagnosticLead - 1)
                ? diagnosticLead - 1
                : 1,
            dataLength: dataSamples ? dataSamples.length : 0,
            isLoadedData: true,
            spdValue: !_.isNil(speed) ? speed : 25,
            ampValue: !_.isNil(amp) ? amp : 10,
          };
          if (hiddenChannels && hiddenChannels.length) {
            _.assign(setStateObject, {
              visibleChannels: hiddenChannels,
              beforeZoomVisibleChannels: hiddenChannels,
            });
          }
          if (!_.isNil(hpFilters)) {
            _.assign(setStateObject, { hiPValue: hpFilters });
          }
          if (!_.isNil(notchFilters)) {
            _.assign(setStateObject, { loPValue: notchFilters });
          }
          if (!_.isNil(isShowBeatMarkers)) {
            _.assign(setStateObject, { isShowBeatMarkers });
          }
          // oxlint-disable-next-line react/no-did-update-set-state
          this.setState(setStateObject);
        }
      }
    }
  }

  createInitialValue = () => ({
    isShowBeatMarkers: true,
    shouldDelete: false,
    isClickFineGrid: false,
    isInARBeatMode: false,
    isInWaitingZoomMode: false,
    isInZoomMode: false,
    isInViewAllMode: false,
    spdValue: 25,
    ampValue: 10,
    hiPValue: 0,
    loPValue: 0,
    prevChanIdx: 1,
    dataLength: 3,
    visibleChannels: [0, 1, 2],
    beforeZoomVisibleChannels: [0, 1, 2],
    openSpdDropdown: false,
    openAmpDropdown: false,
    openHiPDropdown: false,
    openLoPDropdown: false,
    openSHChanDropdown: false,
    openPrevChanDropdown: false,
    openTooltipSpd: false,
    openTooltipAmp: false,
    openTooltipHiP: false,
    openTooltipLoP: false,
    openTooltipSHChan: false,
    openTooltipPrevChan: false,
    openTooltipFineGrid: false,
    openTooltipARBeat: false,
    openTooltipZoom: false,
    openTooltipViewAll: false,
    openTooltipCapture: false,
    isInMode: false,
  });

  getStreamingData = () => this.props.streamingData;

  /**
   * Renders the canvas stack the FabricJS ECG chart draws onto. Emits a background layer holding two canvases plus a waveform canvas and an object canvas, each element id suffixed with the viewer's index prop so several viewers stay individually addressable.
   *
   * @returns The viewer's canvas markup.
   */
  render() {
    return (
      <div id="ecg-viewer" className="ecg-viewer">
        <div id="canvas-chart-container" className="chart-graph">
          <div className="canvas-background">
            <canvas id={`my-canvas1-1-${this.props.index}`} />
            <canvas id={`my-canvas1-2-${this.props.index}`} />
          </div>
          <div className="canvas-chart">
            <canvas id={`my-canvas2-${this.props.index}`} />
          </div>
          <div className="canvas-object">
            <canvas id={`my-canvas3-${this.props.index}`} />
          </div>
        </div>
      </div>
    );
  }
}

(ECGViewer as any).defaultProps = {
  updateMinMaxHr: () => {},
  onCalculatedData: () => {},
  onBpmChanged: () => {},
  timeObject: {},
  index: 1,
  isStreaming: false,
  streamingData: [[], [], []],
  handlePickDate: () => {},
};

export default ECGViewer;
